import React, { useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Language } from '../../i18n';
import { useLanguageStore, useTranslation } from '../../store/languageStore';
import { useAuthStore } from '../../store/authStore';
import authService from '../services/authService';

// Flag emojis for countries
const FLAG_EMOJIS: Record<Language, string> = {
  en: '🇺🇸', // US flag for English
  de: '🇩🇪', // German flag
};

const LANGUAGE_NAMES: Record<Language, { en: string; de: string }> = {
  en: { en: 'English', de: 'Englisch' },
  de: { en: 'German', de: 'Deutsch' },
};

interface LanguageSelectorProps {
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  showLabel?: boolean;
  sx?: any;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'outlined',
  size = 'small',
  showLabel = false,
  sx = {}
}) => {
  const { currentLanguage, setLanguage } = useLanguageStore();
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const handleLanguageChange = async (event: SelectChangeEvent<Language>) => {
    const newLanguage = event.target.value as Language;

    // Update local state immediately for better UX
    setLanguage(newLanguage);

    // Only save to backend if user is authenticated
    if (isAuthenticated) {
      try {
        await authService.updateUserLanguage(newLanguage);

        // Show success message for authenticated users
        toast.success(
          newLanguage === 'en'
            ? 'Language updated to English'
            : 'Sprache auf Deutsch aktualisiert'
        );
      } catch (error) {
        console.error('Failed to update language preference:', error);
        toast.error(
          newLanguage === 'en'
            ? 'Failed to update language preference'
            : 'Spracheinstellung konnte nicht aktualisiert werden'
        );
      }
    }
    // For non-authenticated users, language is still saved locally via Zustand persist
  };

  return (
    <FormControl
      variant={variant}
      size={size}
      sx={{
        minWidth: 100,
        '& .MuiOutlinedInput-root': {
          borderRadius: 1.5,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          height: 32,
        },
        ...sx
      }}
    >
      {showLabel && (
        <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>
          {t('common.language')}
        </Typography>
      )}
      <Select
        value={currentLanguage}
        onChange={handleLanguageChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        displayEmpty
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1 }}>
              {FLAG_EMOJIS[selected as Language]}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {LANGUAGE_NAMES[selected as Language][currentLanguage]}
            </Typography>
          </Box>
        )}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.5,
            fontSize: '0.8rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.2)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {Object.entries(LANGUAGE_NAMES).map(([langCode, names]) => (
          <MenuItem
            key={langCode}
            value={langCode}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              px: 1.5,
              minHeight: 32,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.light,
                '&:hover': {
                  backgroundColor: theme.palette.primary.light,
                },
              },
            }}
          >
            <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
              {FLAG_EMOJIS[langCode as Language]}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {names[currentLanguage]}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;